import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  IconButton,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ActionButtons from "../../../Components/Quetionary/InitialKickoff/MaterialTopics/ActionButtons";
import { useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import { get, put } from "../../../utils/api";
import { numberError, textError } from "../../../utils/validationMessage";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import FileUpload from "./FileUpload";
import DeleteModal from "../../../Components/Common/DeleteModal";
import {
  formatNumberInputWithCommas,
  stripNumberFormatting,
} from "../../../utils/common";

const RiskManagementForm = () => {
  const { id } = useParams();
  const [riskCommittee, setRiskCommittee] = useState("Yes");
  const [ethicsTraining, setEthicsTraining] = useState("yes");
  const [riskCommitteeDocumentUrl, setRiskCommitteeDocumentUrl] = useState("");
  const [moduleType, setModuleType] = useState(""); // "annual" | "quarterly"
  // For Annual: committee details text
  const [riskCommitteeMeetings, setRiskCommitteeMeetings] = useState("");
  const [riskManagementNumber, setRiskManagementNumber] = useState("");
  const navigate = useNavigate();
  const [meetingError, setMeetingError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({ fieldName: "", index: "" });

  // Quarterly tables (as per screenshot)
  const [materialIssuesRows, setMaterialIssuesRows] = useState([
    {
      materialIssueIdentified: "",
      riskOrOpportunity: "",
      rationale: "",
      financialImplications: "",
    },
  ]);
  const [mitigationRows, setMitigationRows] = useState([
    { riskIdentified: "", approachToMitigate: "", riskIdentifiedAuto: false },
  ]);

  const riskRowIndexes = materialIssuesRows
    .map((r, idx) => ({
      idx,
      isRisk: String(r?.riskOrOpportunity || "")
        .toLowerCase()
        .trim()
        .includes("risk"),
    }))
    .filter((x) => x.isRisk)
    .map((x) => x.idx);

  const materialIssueFieldLabels = {
    materialIssueIdentified: "Material issue identified",
    riskOrOpportunity: "Risk / Opportunity (R/O)",
    rationale: "Rationale",
    financialImplications: "Financial implications",
  };

  const mitigationFieldLabels = {
    riskIdentified: "Risk identified",
    approachToMitigate: "Approach to adapt or mitigate",
  };

  const materialTableErrorList = Object.keys(validationErrors || {})
    .filter((k) => k.startsWith("materialIssues."))
    .filter((k) => Boolean(validationErrors?.[k]))
    .map((k) => {
      // materialIssues.{rowIdx}.{field}
      const parts = k.split(".");
      const rowIdx = Number(parts[1]);
      const field = parts[2];
      return {
        key: k,
        row: Number.isFinite(rowIdx) ? rowIdx + 1 : "-",
        field: materialIssueFieldLabels[field] || field,
        message: validationErrors[k],
      };
    });

  const mitigationTableErrorList = Object.keys(validationErrors || {})
    .filter((k) => k.startsWith("mitigation."))
    .filter((k) => Boolean(validationErrors?.[k]))
    .map((k) => {
      // mitigation.{rowIdx}.{field}
      const parts = k.split(".");
      const rowIdx = Number(parts[1]);
      const field = parts[2];
      return {
        key: k,
        row: Number.isFinite(rowIdx) ? rowIdx + 1 : "-",
        field: mitigationFieldLabels[field] || field,
        message: validationErrors[k],
      };
    });

  const isMaterialRowComplete = (row) => {
    return Boolean(
      row?.materialIssueIdentified &&
      row?.riskOrOpportunity &&
      row?.rationale &&
      row?.financialImplications,
    );
  };

  // Disable "Add" until all existing rows are filled
  const canAddMaterialRow = materialIssuesRows.every(isMaterialRowComplete);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/governance/getGovernance`, {
          params: { id: id },
        });

        const record = response.data?.data?.[0] || {};
        const data = record?.riskManagement || {};
        console.log("Fetched Data:", data);

        const rawModule = (
          record?.module ||
          record?._governanceDataRef?.module ||
          record?.entryType ||
          ""
        )
          .toString()
          .toLowerCase()
          .trim();
        if (rawModule.includes("annual")) setModuleType("annual");
        else if (rawModule.includes("quarterly")) setModuleType("quarterly");
        else setModuleType("");

        setRiskCommittee(
          typeof data.hasRiskManagementCommittee === "boolean"
            ? data.hasRiskManagementCommittee
              ? "Yes"
              : "No"
            : "Yes",
        );
        setRiskCommitteeMeetings(data.riskCommitteeMeetings || "");
        setRiskManagementNumber(
          data.riskManagementNumber !== undefined &&
            data.riskManagementNumber !== null &&
            data.riskManagementNumber !== ""
            ? formatNumberInputWithCommas(String(data.riskManagementNumber))
            : "",
        );
        const committeeStatus = data?.hasRiskManagementCommittee?.status;
        setEthicsTraining(
          typeof committeeStatus === "boolean"
            ? committeeStatus
              ? "yes"
              : "no"
            : String(committeeStatus || "").toLowerCase() === "no"
              ? "no"
              : "yes",
        );
        setRiskCommitteeDocumentUrl(
          data?.hasRiskManagementCommittee?.documentUrl || "",
        );

        // Quarterly: hydrate new table structure if present
        const issues =
          data.materialIssuesIdentified ||
          data.materialIssues ||
          data.materialIssueIdentified ||
          [];
        const mitigations =
          data.mitigationApproaches ||
          data.riskMitigationApproaches ||
          data.mitigationPlans ||
          [];

        if (Array.isArray(issues) && issues.length) {
          const normalizedIssues = issues.map((r) => ({
            materialIssueIdentified:
              r.materialIssueIdentified ??
              r.theMaterialIssueIdentified ??
              r.meterialIssueIdentified ??
              r.materialIssue ??
              "",
            riskOrOpportunity:
              r.riskOrOpportunity ??
              r.riskOpportunity ??
              r.riskOrOppertunity ??
              "",
            rationale:
              r.rationale ??
              r.rationaleForIdentifying ??
              r.rationalForIdentifying ??
              "",
            financialImplications:
              r.financialImplications ??
              r.financialImplication ??
              r.financial ??
              "",
          }));
          setMaterialIssuesRows(normalizedIssues);

          // Keep mitigation rows index-aligned with material issues.
          const normalizedMitigations = Array.isArray(mitigations)
            ? mitigations.map((r) => ({
                riskIdentified: r.riskIdentified ?? r.risk ?? "",
                approachToMitigate:
                  r.approachToMitigate ??
                  r.approachToAdaptOrMitigate ??
                  r.approach ??
                  "",
                riskIdentifiedAuto: false,
              }))
            : [];

          let riskCursor = 0;
          const alignedMitigations = normalizedIssues.map((issueRow, idx) => {
            const isRisk = String(issueRow?.riskOrOpportunity || "")
              .toLowerCase()
              .trim()
              .includes("risk");
            const existingByIndex = normalizedMitigations[idx];
            const existingByRiskOrder = normalizedMitigations[riskCursor];
            if (!isRisk) {
              return {
                riskIdentified: "",
                approachToMitigate: "",
                riskIdentifiedAuto: false,
              };
            }
            riskCursor += 1;
            const existing =
              existingByIndex && (existingByIndex.riskIdentified || existingByIndex.approachToMitigate)
                ? existingByIndex
                : existingByRiskOrder && (existingByRiskOrder.riskIdentified || existingByRiskOrder.approachToMitigate)
                  ? existingByRiskOrder
                  : null;
            if (existing) {
              return {
                ...existing,
                // Risk identified field is disabled in UI, keep it auto-synced.
                riskIdentifiedAuto: true,
              };
            }
            const nextRiskFromIssue = issueRow?.materialIssueIdentified || "";
            return {
              riskIdentified: nextRiskFromIssue,
              approachToMitigate: "",
              riskIdentifiedAuto: true,
            };
          });
          setMitigationRows(alignedMitigations);
        } else if (Array.isArray(mitigations) && mitigations.length) {
          setMitigationRows(
            mitigations.map((r) => ({
              riskIdentified: r.riskIdentified ?? r.risk ?? "",
              approachToMitigate: r.approachToMitigate ?? r.approach ?? "",
              riskIdentifiedAuto: false,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error.response || error);
      }
    };

    fetchData();
  }, []);

  const clearFieldError = (key) => {
    setValidationErrors((prev) => ({ ...prev, [key]: "" }));
  };

  useEffect(() => {
    // Drop stale mitigation errors for rows that are no longer "Risk"
    // or no longer exist after row edits/deletes.
    setValidationErrors((prev) => {
      const riskIdxSet = new Set(riskRowIndexes);
      let changed = false;
      const next = { ...prev };

      Object.keys(prev || {}).forEach((k) => {
        if (!k.startsWith("mitigation.")) return;
        const parts = k.split(".");
        const idx = Number(parts[1]);
        if (!Number.isFinite(idx) || !riskIdxSet.has(idx)) {
          delete next[k];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [riskRowIndexes, materialIssuesRows.length]);

  const flatTableTextFieldSx = {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiInputBase-root": {
      minHeight: 36,
      backgroundColor: "transparent",
      alignItems: "flex-start",
      margin: 0,
    },
    "& .MuiInputBase-input:not(.MuiSelect-select)": {
      paddingTop: "6px !important",
      paddingBottom: "6px !important",
      lineHeight: 1.4,
      boxSizing: "border-box",
    },
    "& .MuiInputBase-inputMultiline": {
      paddingTop: "6px !important",
      paddingBottom: "6px !important",
      lineHeight: 1.4,
      boxSizing: "border-box",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    },
  };

  const flatTableSelectSx = {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiInputBase-root": {
      height: 36,
      backgroundColor: "transparent",
      alignItems: "center",
      margin: 0,
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      minHeight: "36px !important",
      lineHeight: 1.4,
      boxSizing: "border-box",
      paddingTop: "8px !important",
      paddingBottom: "12px !important",
    },
  };

  // Quarterly handlers
  const handleMaterialIssueChange = (rowIdx, key, value) => {
    // Capture the next row state so Table-2 auto-fill uses the latest values
    // (avoid stale reads from `materialIssuesRows`).
    const currentRow = materialIssuesRows[rowIdx] || {};
    const nextRow = { ...currentRow, [key]: value };

    setMaterialIssuesRows((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...(prev[rowIdx] || {}), [key]: value };
      return next;
    });
    clearFieldError(`materialIssues.${rowIdx}.${key}`);

    // If user selected "Risk", auto-fill Table 2 riskIdentified from materialIssueIdentified
    if (key === "riskOrOpportunity" || key === "materialIssueIdentified") {
      setMitigationRows((prev) => {
        const next = [...prev];
        const nextMaterialIssueIdentified = nextRow.materialIssueIdentified;
        const nextRiskOrOpp = nextRow.riskOrOpportunity;

        const isRisk = String(nextRiskOrOpp || "")
          .toLowerCase()
          .trim()
          .includes("risk");

        if (isRisk) {
          if (!next[rowIdx]) {
            next[rowIdx] = {
              riskIdentified: "",
              approachToMitigate: "",
              riskIdentifiedAuto: false,
            };
          }
          // Always mirror Table-1 material issue into disabled Table-2 risk field.
          next[rowIdx] = {
            ...next[rowIdx],
            riskIdentified: nextMaterialIssueIdentified || "",
            riskIdentifiedAuto: true,
          };
        } else {
          // If user switches away from Risk, clear the aligned mitigation row.
          if (next[rowIdx]) {
            next[rowIdx] = {
              riskIdentified: "",
              approachToMitigate: "",
              riskIdentifiedAuto: false,
            };
          }
        }
        return next;
      });
      clearFieldError(`mitigation.${rowIdx}.riskIdentified`);
    }
  };

  const handleAddMaterialIssueRow = () => {
    if (!canAddMaterialRow) return;
    setMaterialIssuesRows((prev) => [
      ...prev,
      {
        materialIssueIdentified: "",
        riskOrOpportunity: "",
        rationale: "",
        financialImplications: "",
      },
    ]);
    // Keep a matching mitigation row (autofetch list)
    setMitigationRows((prev) => [
      ...prev,
      { riskIdentified: "", approachToMitigate: "", riskIdentifiedAuto: false },
    ]);
  };

  const handleMitigationChange = (rowIdx, key, value) => {
    setMitigationRows((prev) => {
      const next = [...prev];
      next[rowIdx] = {
        ...next[rowIdx],
        [key]: value,
        ...(key === "riskIdentified" ? { riskIdentifiedAuto: false } : {}),
      };
      return next;
    });
    clearFieldError(`mitigation.${rowIdx}.${key}`);
  };

  // Add from Table 2 should also add a corresponding row in Table 1 (keep tables aligned)
  const handleAddMitigationRow = () => {
    setMaterialIssuesRows((prev) => [
      ...prev,
      {
        materialIssueIdentified: "",
        riskOrOpportunity: "",
        rationale: "",
        financialImplications: "",
      },
    ]);
    setMitigationRows((prev) => [
      ...prev,
      { riskIdentified: "", approachToMitigate: "", riskIdentifiedAuto: false },
    ]);
  };

  // (Removed old Table-2-only add to keep both tables aligned)

  const deleteMaterialIssueRow = (rowIdx) => {
    // Delete the corresponding row in both tables, but keep at least 1 row
    setMaterialIssuesRows((prev) => {
      if (prev.length <= 1) return prev;
      const next = [...prev];
      next.splice(rowIdx, 1);
      return next.length
        ? next
        : [
            {
              materialIssueIdentified: "",
              riskOrOpportunity: "",
              rationale: "",
              financialImplications: "",
            },
          ];
    });
    setMitigationRows((prev) => {
      const next = [...prev];
      if (next.length <= 1) return next; // keep at least 1 row
      if (next.length > rowIdx) next.splice(rowIdx, 1);
      return next.length
        ? next
        : [
            {
              riskIdentified: "",
              approachToMitigate: "",
              riskIdentifiedAuto: false,
            },
          ];
    });
  };

  const handleDelete = () => {
    const fieldName = deleteData.fieldName;
    const rowIdx = Number(deleteData.index);
    if (fieldName !== "materialIssuesRows" || Number.isNaN(rowIdx)) {
      setDeleteDialog(false);
      return;
    }
    deleteMaterialIssueRow(rowIdx);
    setDeleteDialog(false);
    setDeleteData({ fieldName: "", index: "" });
  };

  const deleteMitigationRow = (rowIdx) => {
    setMitigationRows((prev) => {
      if (prev.length <= 1) return prev;
      const next = [...prev];
      next.splice(rowIdx, 1);
      return next.length
        ? next
        : [
            {
              riskIdentified: "",
              approachToMitigate: "",
              riskIdentifiedAuto: false,
            },
          ];
    });
  };

  const handleRiskCommitteeMeetingsChange = (value) => {
    // const formatted = formatNumberInputWithCommas(value);
    // const raw = stripNumberFormatting(formatted);
    // if (raw !== "" && !/^\d+$/.test(raw)) return;
    // if (isNaN(value) || parseFloat(value) <= 0) {
    //   setMeetingError(numberError);
    // } else {
    //   setMeetingError("");
    // }
    setMeetingError("");
    setRiskCommitteeMeetings(value);
    setValidationErrors({ ...validationErrors, riskCommitteeMeetings: "" });
  };

  const isNumberValid = (value) => {
    return /^\d+$/.test(stripNumberFormatting(value)); // positive integer only
  };

  const validateForm = () => {
    const errors = {};

    // Annual screen in image only has committee + details + meetings count
    if (moduleType === "annual") {
      if (!riskCommittee) {
        errors.riskCommittee = "This field is required";
      }

      // If committee exists, additional details become mandatory
      if (riskCommittee === "Yes" && !riskCommitteeMeetings) {
        errors.riskCommitteeMeetings = "This field is required";
      }

      if (riskCommittee === "Yes") {
        if (riskManagementNumber === "") {
          errors.riskManagementNumber = "This field is required";
        } else if (!isNumberValid(riskManagementNumber)) {
          errors.riskManagementNumber = numberError;
        }
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    }

    // Quarterly: validate the two tables
    // if (
    //   ethicsTraining === "yes" &&
    //   !String(riskCommitteeDocumentUrl || "").trim()
    // ) {
    //   errors.riskCommitteeDocumentUrl = "Document upload is mandatory.";
    // }

    materialIssuesRows.forEach((row, idx) => {
      if (!row.materialIssueIdentified)
        errors[`materialIssues.${idx}.materialIssueIdentified`] = textError;
      if (!row.riskOrOpportunity)
        errors[`materialIssues.${idx}.riskOrOpportunity`] = textError;
      if (!row.rationale) errors[`materialIssues.${idx}.rationale`] = textError;
      if (!row.financialImplications)
        errors[`materialIssues.${idx}.financialImplications`] = textError;
    });

    // Validate Table 2 only for rows where Table 1 selected "Risk"
    riskRowIndexes.forEach((idx) => {
      const row = mitigationRows[idx] || {};
      if (!row.riskIdentified)
        errors[`mitigation.${idx}.riskIdentified`] = textError;
      if (!row.approachToMitigate)
        errors[`mitigation.${idx}.approachToMitigate`] = textError;
    });
    console.log("errors", errors);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRiskManagementNumberChange = (value) => {
    const formatted = formatNumberInputWithCommas(value);
    const raw = stripNumberFormatting(formatted);
    // Allow only digits while typing
    if (raw !== "" && !/^\d+$/.test(raw)) return;

    setRiskManagementNumber(formatted);

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      riskManagementNumber:
        formatted === ""
          ? "This field is required"
          : isNumberValid(formatted)
            ? ""
            : numberError,
    }));
  };

  const Move = async () => {
    console.log("validateForm", validateForm());
    if (!validateForm()) {
      return;
    }

    const mitigationPayload = riskRowIndexes.map((idx) => {
      const r = mitigationRows[idx] || {};
      return {
        riskIdentified: r.riskIdentified || "",
        approachToMitigate: r.approachToMitigate || "",
      };
    });

    const annualRiskManagementData = {
      hasRiskManagementCommittee: riskCommittee === "Yes",
      riskCommitteeMeetings: stripNumberFormatting(riskCommitteeMeetings),
      riskManagementNumber: stripNumberFormatting(riskManagementNumber),
    };

    const quarterlyRiskManagementData = {
      materialIssuesIdentified: materialIssuesRows,
      mitigationApproaches: mitigationPayload,
      hasRiskManagementCommittee: {
        status: ethicsTraining,
        // documentUrl: ethicsTraining === "yes" ? riskCommitteeDocumentUrl : "",
      },
    };

    const annualPayload = {
      id,
      module: "annual",
      riskManagement: annualRiskManagementData,
      stepsCompleted: { riskManagement: true },
    };

    const quarterlyPayload = {
      id,
      module: "quarterly",
      riskManagement: quarterlyRiskManagementData,
      stepsCompleted: { riskManagement: true },
    };

    const requestPayload =
      moduleType === "annual" ? annualPayload : quarterlyPayload;

    try {
      const response = await put(
        `/governance/updateGovernance`,
        requestPayload,
      );

      console.log("Data saved successfully:", response.data);
      if (moduleType === "annual") {
        navigate(`/governance/data/shareholder-rights/${id}`);
      } else {
        navigate(`/governance/data/compliance-ethics/${id}`);
      }
    } catch (error) {
      console.error("Error saving data:", error.response || error);
    }
  };

  const Back = async () => {
    // Annual flow goes through Executive Compensation; Quarterly does not.
    if (moduleType === "annual") {
      navigate(`/governance/data/executive-compensation/${id}`);
    } else {
      navigate(`/governance/data/board-structure/${id}`);
    }
  };

  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <DeleteModal
        onOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
      />
      <Box
        sx={{
          margin: "20px auto",
          padding: "20px",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: "#1976d2",
            },
        }}
      >
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{
            fontWeight: "bolder",
            margin: "1.2% 0",
            textDecoration: "underline",
          }}
        >
          B. RISK MANAGEMENT
        </Typography>

        {/* Annual UI (as per image): only Q1 and Q2 */}
        {moduleType === "annual" ? (
          <>
            <FormControl sx={{ marginBottom: "16px" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", margin: "1% 0" }}
              >
                1. Existence of Risk Management Committe
              </Typography>
              <RadioGroup
                row
                sx={{ ml: "1.1rem" }}
                value={riskCommittee}
                onChange={(e) => setRiskCommittee(e.target.value)}
              >
                <FormControlLabel
                  value="Yes"
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  value="No"
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />
                  }
                  label="No"
                />
              </RadioGroup>
              {validationErrors.riskCommittee && (
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                  {validationErrors.riskCommittee}
                </FormHelperText>
              )}
            </FormControl>

            {riskCommittee === "Yes" && (
              <Box sx={{ width: "55%", marginBottom: "16px" }}>
                <TextField
                  value={riskCommitteeMeetings}
                  onChange={(e) =>
                    handleRiskCommitteeMeetingsChange(e.target.value)
                  }
                  placeholder="Enter Additional Text"
                  variant="outlined"
                  fullWidth
                  error={!!validationErrors.riskCommitteeMeetings}
                  multiline
                  minRows={1}
                  InputProps={{
                    sx: {
                      padding: "9px 12px",
                      ml: "1.1rem",
                    },
                  }}
                />
                {validationErrors.riskCommitteeMeetings && (
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      fontSize: "14px",
                      marginLeft: "1.1rem",
                    }}
                  >
                    {validationErrors.riskCommitteeMeetings}
                  </FormHelperText>
                )}
              </Box>
            )}

{riskCommittee === "Yes" && <Box sx={{ marginBottom: "16px", width: "55%" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", margin: "1% 0" }}
              >
                2. Number of Risk Management Committee Meetings
              </Typography>
              <TextField
                value={riskManagementNumber}
                onChange={(e) =>
                  handleRiskManagementNumberChange(e.target.value)
                }
                placeholder="Enter Number"
                variant="outlined"
                fullWidth
                error={!!validationErrors.riskManagementNumber}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 40,
                  },
                  ml: "1.1rem",
                }}
              />
              {validationErrors.riskManagementNumber && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    fontSize: "14px",
                    marginLeft: "1.1rem",
                  }}
                >
                  {validationErrors.riskManagementNumber}
                </FormHelperText>
              )}
            </Box>}

            <Box
              sx={{
                width: "100%",
                mt: "10%",
                mb: 5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<WestIcon />}
                sx={{
                  textTransform: "none",
                  borderColor: "#1C486B",
                  color: "#1C486B",
                  "&:hover": {
                    borderColor: "#1C486B",
                    backgroundColor: "rgba(28, 72, 107, 0.06)",
                  },
                }}
                onClick={Back}
              >
                Back
              </Button>
              <Button
                variant="contained"
                endIcon={<EastIcon />}
                sx={{ bgcolor: "#1C486B", textTransform: "none" }}
                onClick={Move}
              >
                Save & continue
              </Button>
            </Box>
          </>
        ) : moduleType === "quarterly" ? (
          <>
            {/* Quarterly UI as per screenshot: Table 1 */}
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", mt: 1, mb: 1 }}
            >
              1. Materiality Assessment
            </Typography>
            <TableContainer component={Paper} sx={{ width: "100%" }}>
              <Table
                sx={{
                  borderCollapse: "collapse",
                  tableLayout: "fixed",
                  width: "100%",
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "Material issue identified",
                      "Indicate whether risk or opportunity",
                      "Rationale for identifying the risk / opportunity",
                      "Financial implications",
                      "Action",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 700,
                          backgroundColor: "#1C486B",
                          color: "#fff",
                          borderRight: "1px solid rgba(255,255,255,0.25)",
                          width:
                            h === "Material issue identified"
                              ? "24%"
                              : h === "Indicate whether risk or opportunity"
                                ? "19%"
                                : h ===
                                    "Rationale for identifying the risk / opportunity"
                                  ? "29%"
                                  : h === "Financial implications"
                                    ? "18%"
                                    : "10%",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materialIssuesRows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        padding: "0px",
                        margin: "0px",
                      }}
                    >
                      {[
                        "materialIssueIdentified",
                        "riskOrOpportunity",
                        "rationale",
                        "financialImplications",
                      ].map((key) => (
                        <TableCell
                          key={key}
                          sx={{
                            borderRight: "1px solid #E0E0E0",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            verticalAlign: "middle",
                            padding: "0px",
                            margin: "0px",
                          }}
                        >
                          {key === "riskOrOpportunity" ? (
                            <FormControl
                              fullWidth
                              error={
                                !!validationErrors[
                                  `materialIssues.${idx}.riskOrOpportunity`
                                ]
                              }
                            >
                              <Select
                                value={row.riskOrOpportunity}
                                onChange={(e) =>
                                  handleMaterialIssueChange(
                                    idx,
                                    "riskOrOpportunity",
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                fullWidth
                                sx={flatTableSelectSx}
                              >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Risk">Risk</MenuItem>
                                <MenuItem value="Opportunity">
                                  Opportunity
                                </MenuItem>
                              </Select>
                            </FormControl>
                          ) : key === "financialImplications" ? (
                            <FormControl
                              fullWidth
                              error={
                                !!validationErrors[
                                  `materialIssues.${idx}.financialImplications`
                                ]
                              }
                            >
                              <Select
                                value={row.financialImplications}
                                onChange={(e) =>
                                  handleMaterialIssueChange(
                                    idx,
                                    "financialImplications",
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                fullWidth
                                sx={flatTableSelectSx}
                              >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Positive">Positive</MenuItem>
                                <MenuItem value="Negative">Negative</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <TextField
                              value={row[key]}
                              onChange={(e) =>
                                handleMaterialIssueChange(
                                  idx,
                                  key,
                                  e.target.value,
                                )
                              }
                              placeholder="Enter"
                              variant="outlined"
                              multiline
                              minRows={1}
                              maxRows={4}
                              fullWidth
                              error={
                                !!validationErrors[
                                  `materialIssues.${idx}.${key}`
                                ]
                              }
                              sx={{
                                ...flatTableTextFieldSx,
                                "& .MuiInputBase-input": {
                                  whiteSpace: "normal",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                },
                              }}
                            />
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ width: 60, textAlign: "center" }}>
                        <IconButton
                          onClick={() => {
                            setDeleteDialog(true);
                            setDeleteData({
                              fieldName: "materialIssuesRows",
                              index: idx,
                            });
                          }}
                          disabled={materialIssuesRows.length <= 1}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {materialTableErrorList.length > 0 && (
              <Box sx={{ width: "100%", mt: 1 }}>
                {materialTableErrorList.map((e) => (
                  <FormHelperText key={e.key} sx={{ color: "#D34040" }}>
                    {`Row ${e.row} - ${e.field}: ${e.message}`}
                  </FormHelperText>
                ))}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                mt: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddMaterialIssueRow}
                disabled={!canAddMaterialRow}
                sx={{
                  textTransform: "none",
                  color: !canAddMaterialRow ? "#cbd2d8" : "#1C486B",
                  borderColor: !canAddMaterialRow ? "#cbd2d8" : "#1C486B",
                }}
              >
                Add
              </Button>
            </Box>

            {/* Quarterly UI as per screenshot: Table 2 */}
            {riskRowIndexes.length > 0 && (
              <>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", mt: 4, mb: 1 }}
                >
                  2. Risk Management
                </Typography>
                <TableContainer component={Paper} sx={{ width: "100%" }}>
                  <Table sx={{ borderCollapse: "collapse" }}>
                    <TableHead>
                      <TableRow>
                        {[
                          "Risk identified",
                          "Approach to adapt or mitigate",
                        ].map((h) => (
                          <TableCell
                            key={h}
                            sx={{
                              fontWeight: 700,
                              backgroundColor: "#1C486B",
                              color: "#fff",
                              borderRight: "1px solid rgba(255,255,255,0.25)",
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {riskRowIndexes.map((origIdx) => {
                        const row = mitigationRows[origIdx] || {
                          riskIdentified: "",
                          approachToMitigate: "",
                          riskIdentifiedAuto: false,
                        };
                        return (
                          <TableRow key={origIdx}>
                            <TableCell
                              sx={{
                                borderRight: "1px solid #E0E0E0",
                                width: "40%",
                              }}
                            >
                              <TextField
                                value={row.riskIdentified}
                                placeholder="Enter"
                                variant="outlined"
                                fullWidth
                                disabled
                                error={
                                  !!validationErrors[
                                    `mitigation.${origIdx}.riskIdentified`
                                  ]
                                }
                                sx={flatTableTextFieldSx}
                              />
                            </TableCell>
                            <TableCell
                              sx={{ borderRight: "1px solid #E0E0E0" }}
                            >
                              <TextField
                                value={row.approachToMitigate}
                                onChange={(e) =>
                                  handleMitigationChange(
                                    origIdx,
                                    "approachToMitigate",
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter"
                                variant="outlined"
                                fullWidth
                                error={
                                  !!validationErrors[
                                    `mitigation.${origIdx}.approachToMitigate`
                                  ]
                                }
                                sx={flatTableTextFieldSx}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {mitigationTableErrorList.length > 0 && (
                  <Box sx={{ width: "100%", mt: 1 }}>
                    {mitigationTableErrorList.map((e) => (
                      <FormHelperText key={e.key} sx={{ color: "#D34040" }}>
                        {`Row ${e.row} - ${e.field}: ${e.message}`}
                      </FormHelperText>
                    ))}
                  </Box>
                )}
              </>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                mt: 2,
              }}
            >
              {/* <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddMitigationRow}
                sx={{ textTransform: "none" }}
              >
                Add
              </Button> */}
            </Box>

            {/* <Box sx={{ margin: "2% 0 1% 0" }}>
              <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
                3. Existence of Risk Management Committee
              </Typography>
              <RadioGroup
                row
                sx={{ ml: "1.1rem" }}
                value={ethicsTraining}
                onChange={(e) => {
                  setEthicsTraining(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    riskCommitteeDocumentUrl: "",
                  }));
                }}
              >
                <FormControlLabel
                  value="yes"
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />
                  }
                  label="No"
                />
              </RadioGroup>

              {ethicsTraining === "yes" && (
                <Box sx={{ ml: "1.1rem", mt: 1 }}>
                  <Typography sx={{ fontWeight: "bold", mb: "4px" }}>
                    Upload evidence
                  </Typography>
                  <FileUpload
                    setDocumentUrl={(fileUrl) => {
                      setRiskCommitteeDocumentUrl(fileUrl || "");
                      setValidationErrors((prev) => ({
                        ...prev,
                        riskCommitteeDocumentUrl: "",
                      }));
                    }}
                    filesList={riskCommitteeDocumentUrl}
                    title=""
                    ml={0}
                    mt={0}
                    marginTop={0}
                    isDisabled
                  />
                  {validationErrors.riskCommitteeDocumentUrl && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {validationErrors.riskCommitteeDocumentUrl}
                    </FormHelperText>
                  )}
                </Box>
              )}
            </Box> */}

            <Box
              sx={{
                width: "100%",
                mt: "10%",
                mb: 5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<WestIcon />}
                sx={{
                  textTransform: "none",
                  borderColor: "#1C486B",
                  color: "#1C486B",
                  "&:hover": {
                    borderColor: "#1C486B",
                    backgroundColor: "rgba(28, 72, 107, 0.06)",
                  },
                }}
                onClick={Back}
              >
                Back
              </Button>
              <Button
                variant="contained"
                endIcon={<EastIcon />}
                sx={{ bgcolor: "#1C486B", textTransform: "none" }}
                onClick={Move}
              >
                Save & continue
              </Button>
            </Box>
          </>
        ) : null}
      </Box>
    </MainDashboardLayout>
  );
};

export default RiskManagementForm;
